import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { HOME_CONTENT_MOCK } from '../data/home-content.mock';
import type {
  HomePasosBannersActivosResponse,
  HomePasosContent,
} from '../models/home-pasos-content.model';
import {
  mapPasosBannersToContent,
  PASOS_IMAGE_DESKTOP_FALLBACK,
} from '../utils/home-pasos-banner.mapper';
import { preparePasosTitleHtml } from '../utils/home-pasos-html.util';

/** Debe coincidir con `NeoBannerDTO.PASOS_CREDITO` */
export const PASOS_BANNER_TIPO = 'PasosCredito';

@Injectable({
  providedIn: 'root',
})
export class HomePasosService {
  private readonly fallbackContent: HomePasosContent = buildPasosFallbackFromMock();

  constructor(private readonly bancoldexService: BancoldexService) {}

  loadContent(): Observable<HomePasosContent> {
    return this.bancoldexService.getArchivosBannerActivos(PASOS_BANNER_TIPO).pipe(
      map((response: HomePasosBannersActivosResponse) => {
        const rows = normalizePasosBannerRows(response);
        if (!rows.length) {
          return this.fallbackContent;
        }
        return mapPasosBannersToContent(rows, this.fallbackContent);
      }),
      catchError(() => of(this.fallbackContent))
    );
  }
}

function buildPasosFallbackFromMock(): HomePasosContent {
  const mock = HOME_CONTENT_MOCK.pasos;
  return {
    eyebrow: mock.eyebrow,
    titleHtml: preparePasosTitleHtml(mock.titleHtml),
    items: mock.items.map((item) => ({ ...item })),
    imageDesktopSrc: PASOS_IMAGE_DESKTOP_FALLBACK,
  };
}

/** El API devuelve lista plana para `PasosCredito`; otros tipos pueden venir agrupados. */
function normalizePasosBannerRows(
  response: HomePasosBannersActivosResponse
): HomePasosBannersActivosResponse['banners'] {
  if (!response?.exitoso || !response.banners?.length) {
    return [];
  }

  const first = response.banners[0] as unknown;
  if (Array.isArray(first)) {
    return (response.banners as unknown as HomePasosBannersActivosResponse['banners'][]).flat();
  }

  return response.banners;
}
